/** Mentor join: recording and upload guide */
export default {
	common: {
		kicker: "Instructions",
		title: "Recording and Audio File Upload Guide",
		summary:
			"Select the section for your device. After recording, check the audio quality, file format, file size, and network connection before uploading.",
		close: "Close",
	},
	sidePanel: {
		keyReminders: {
			title: "Key Reminders",
			items: [
				"Confirm that the microphone can record properly before starting.",
				"Choose a quiet environment and avoid long silent sections.",
				"Include the name and date in the file name if possible.",
				"After uploading, use the success prompt on the page as confirmation.",
			],
		},
		formats: {
			title: "Recommended Formats",
			chips: ["MP3", "M4A", "AAC", "MP4", "3GP", "3G2"],
		},
		fileSize: {
			title: "File Size",
			items: [
				"Maximum supported size: 5 MB.",
				"If the file is too large, compress it or record again before uploading.",
			],
		},
	},
	checklist: {
		title: "Pre-upload Checklist",
		rows: [
			{
				label: "Audio Quality",
				value: "The main speech should be clear, with no long silence or excessive noise.",
			},
			{
				label: "Format",
				value:
					"Supported formats include MP3, M4A, AAC, MP4, 3GP, and 3G2. MP3 or M4A is recommended.",
			},
			{
				label: "Size",
				value:
					"The maximum supported file size is 5 MB. If the file is too large, compress it according to platform requirements before uploading.",
			},
			{
				label: "Network",
				value:
					"Keep the network connection stable during upload and avoid closing the page midway.",
			},
		],
	},
	faq: {
		title: "FAQ",
		items: [
			{
				label: "Cannot find the recording:",
				text: "Check the sharing, export, or file location options in the recording app.",
			},
			{
				label: "Unable to upload:",
				text: "Confirm that the file format and size meet the platform limits.",
			},
			{
				label: "Recording volume is too low:",
				text: "Record again closer to the microphone in a quiet environment.",
			},
			{
				label: "Unsure whether the upload succeeded:",
				text: "Check the page prompt. If still unsure, upload the file again.",
			},
		],
	},
	mobile: {
		tabs: {
			iphone: "iPhone Recording",
			android: "Android Recording",
			computer: "Computer Recording",
			checklist: "Checklist",
			faq: "FAQ",
		},
		iphone: {
			title: "iPhone Recording",
			note: "Record close to the microphone and avoid background noise where possible. Use a clear file name such as Name_Date_Recording for easier identification.",
			steps: [
				{
					paragraphs: ["Open the Voice Memos app."],
					imageKey: "step1",
					imageLayout: "single",
					imageAlts: ["Open the Voice Memos app"],
				},
				{
					paragraphs: [
						"Tap the red record button to start recording.",
						"Stay close to the microphone and avoid excessive background noise.",
					],
					imageKey: "step2",
					imageLayout: "single",
					imageAlts: ["Tap the red record button"],
				},
				{
					paragraphs: ["Tap the stop button when finished."],
					imageKey: "step3",
					imageLayout: "single",
					imageAlts: ["Tap the stop button"],
				},
				{
					paragraphs: [
						"Optional: tap the recording name and rename it so it is easy to identify.",
						"Example: Name_Date_Recording.",
					],
					imageKey: "step4",
					imageLayout: "row",
					imageAlts: ["Rename recording step 1", "Rename recording step 2"],
				},
				{
					paragraphs: ["After recording, save the audio file to the Files folder on your phone."],
					subSteps: [
						"Tap the Select button.",
						"Select the recording you just made, then tap the share button.",
						"Tap Save to Files.",
						"Tap Save to store the audio file in your phone's Files folder.",
					],
					imageKey: "step5",
					imageLayout: "row",
					imageAlts: ["Tap Select", "Select recording and share", "Save to Files", "Save complete"],
				},
				{
					paragraphs: [
						"After confirming the file has been saved, tap Click to Upload on the page, then tap Choose File.",
						"Select the audio file you just recorded and saved. When the page shows Recording Uploaded Successfully, the upload is complete. To upload again, tap the delete icon to remove the original file, then choose and upload the audio file again.",
					],
					imageKey: "step6",
					imageLayout: "row",
					imageAlts: ["Tap upload button", "Upload successful", "Remove audio file"],
				},
			],
		},
		android: {
			title: "Android Phone Recording",
			note: "The steps below use a Samsung phone as an example. Recording app names may vary by phone brand, so follow the actual screen on your device.",
			steps: [
				{
					paragraphs: [
						"Open the built-in Recorder, Voice Recorder, or similarly named app on your phone.",
					],
					imageKey: "step1",
					imageLayout: "single",
					imageAlts: ["Open Android recording app"],
				},
				{
					paragraphs: [
						"Tap the record button to start recording.",
						"Keep the phone steady and record in a quiet environment whenever possible.",
					],
					imageKey: "step2",
					imageLayout: "single",
					imageAlts: ["Android tap record button"],
				},
				{
					paragraphs: ["Tap Stop or Save when finished."],
					imageKey: "step3",
					imageLayout: "single",
					imageAlts: ["Android finish recording"],
				},
				{
					paragraphs: [
						"Optional: rename the file so it is easy to identify.",
						"Example: Name_Date_Recording.",
					],
					imageKey: "step4",
					imageLayout: "row",
					imageAlts: ["Android rename step 1", "Android rename step 2", "Android rename complete"],
				},
				{
					paragraphs: [
						"After confirming the file has been saved, tap Click to Upload on the page and follow the prompts to select the audio file.",
						"When the page shows Recording Uploaded Successfully, the upload is complete. To upload again, tap the delete icon to remove the original file, then choose and upload the audio file again.",
					],
					imageKey: "step5",
					imageLayout: "row",
					imageAlts: [
						"Tap upload button",
						"Android choose file step 1",
						"Android choose file step 2",
						"Android select audio file",
						"Android upload successful",
						"Remove audio file",
					],
				},
				{
					paragraphs: [
						"Note: On some Android phones, after tapping Click to Upload, you can record directly with the system recorder and upload the recording.",
						"If your phone supports this feature, tap Recorder directly, record for 60 to 300 seconds, and then upload.",
					],
					imageKey: "step6",
					imageLayout: "single",
					imageAlts: ["Android record and upload directly"],
				},
			],
		},
	},
	pc: {
		tabs: {
			iphone: "iPhone Recording",
			android: "Android Recording",
			computer: "Computer Recording",
			checklist: "Checklist",
			faq: "FAQ",
		},
		computer: {
			title: "Computer Recording",
			note: "We recommend recording and uploading from a phone. If you need to record on a computer, first confirm that the microphone and browser upload function work properly.",
			windows: {
				title: "Windows Computer Recording",
				steps: [
					{
						paragraphs: ["Search for and open Recorder or Sound Recorder from the Start menu."],
					},
					{ paragraphs: ["Make sure the microphone is connected and working properly."] },
					{ paragraphs: ["Tap the record button to start recording."] },
					{
						paragraphs: [
							"Tap Stop when finished. The system will save the recording automatically.",
						],
					},
					{
						paragraphs: [
							"Find the file in the recording list and rename it so it is easy to identify.",
						],
					},
					{
						paragraphs: [
							"After confirming the file has been saved, tap Click to Upload on the page and follow the prompts to select the audio file.",
							"When the page shows Recording Uploaded Successfully, the upload is complete. To upload again, tap the delete icon to remove the original file, then choose and upload the audio file again.",
						],
						imageKey: "upload",
						imageLayout: "row",
						imageAlts: [
							"Upload on computer",
							"Choose audio file on computer",
							"Upload successful on computer",
						],
					},
				],
			},
			mac: {
				title: "Mac Computer Recording",
				steps: [
					{ paragraphs: ["Open QuickTime Player."] },
					{
						paragraphs: ["From the top menu, choose File > New Audio Recording."],
					},
					{ paragraphs: ["Tap the record button to start recording."] },
					{ paragraphs: ["Tap Stop when finished."] },
					{
						paragraphs: [
							"Choose File > Save, enter a clear file name, and select a save location.",
						],
					},
					{
						paragraphs: [
							"After confirming the file has been saved, tap Click to Upload on the page and follow the prompts to select the audio file.",
							"When the page shows Recording Uploaded Successfully, the upload is complete. To upload again, tap the delete icon to remove the original file, then choose and upload the audio file again.",
						],
						imageKey: "macUpload",
						imageLayout: "row",
						imageAlts: [
							"Upload on computer",
							"Choose audio file on computer",
							"Upload successful on computer",
						],
					},
				],
			},
		},
	},
};
